import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import {
  MAX_VOICE_MESSAGE_BYTES,
  MAX_VOICE_MESSAGE_DURATION_MS,
} from "../../shared/game.ts";
import { getVoiceMessageAudio, uploadVoiceMessage } from "../lib/api.ts";
import { getGameToken } from "../lib/storage.ts";
import { Icon } from "./Icon.tsx";

const VOICE_MIME_TYPES = [
  "audio/webm;codecs=opus",
  "audio/mp4",
  "audio/ogg;codecs=opus",
] as const;

type RecorderStatus = "idle" | "requesting" | "recording" | "preview" | "uploading";

interface VoiceDraft {
  blob: Blob;
  durationMs: number;
  url: string;
}

interface LoadedVoiceSource {
  url: string;
  revoke: boolean;
}

function formatDuration(durationMs: number): string {
  const seconds = Math.max(0, Math.round(durationMs / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

function preferredMimeType(): string | null {
  if (typeof MediaRecorder === "undefined") return null;
  return VOICE_MIME_TYPES.find((mimeType) => MediaRecorder.isTypeSupported(mimeType)) ?? null;
}

function microphoneError(error: unknown): string {
  if (error instanceof DOMException && ["NotAllowedError", "SecurityError"].includes(error.name)) {
    return "Разрешите приложению доступ к микрофону и попробуйте ещё раз.";
  }
  if (error instanceof DOMException && error.name === "NotFoundError") {
    return "Микрофон на этом устройстве не найден.";
  }
  return "Не удалось начать запись. Проверьте микрофон и попробуйте ещё раз.";
}

function VoicePlayer({
  durationMs,
  loadSource,
  label,
}: {
  durationMs: number;
  loadSource: () => Promise<LoadedVoiceSource>;
  label: string;
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const ownedUrlRef = useRef<string | null>(null);
  const mountedRef = useRef(true);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [playing, setPlaying] = useState(false);
  const [currentMs, setCurrentMs] = useState(0);
  const [knownDurationMs, setKnownDurationMs] = useState(durationMs);

  const releaseAudio = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.onplay = null;
      audio.onpause = null;
      audio.onended = null;
      audio.ontimeupdate = null;
      audio.onloadedmetadata = null;
      audio.onerror = null;
      audio.pause();
      audio.removeAttribute("src");
      audio.load();
      audioRef.current = null;
    }
    if (ownedUrlRef.current) {
      URL.revokeObjectURL(ownedUrlRef.current);
      ownedUrlRef.current = null;
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      releaseAudio();
    };
  }, [releaseAudio]);

  const prepareAudio = useCallback(async (): Promise<HTMLAudioElement> => {
    if (audioRef.current) return audioRef.current;
    setLoadState("loading");
    const source = await loadSource();
    if (!mountedRef.current) {
      if (source.revoke) URL.revokeObjectURL(source.url);
      throw new Error("player_unmounted");
    }

    const audio = new Audio(source.url);
    audio.preload = "metadata";
    audio.onplay = () => {
      setPlaying(true);
      setLoadState("ready");
    };
    audio.onpause = () => setPlaying(false);
    audio.onended = () => {
      audio.currentTime = 0;
      setPlaying(false);
      setCurrentMs(0);
    };
    audio.ontimeupdate = () => setCurrentMs(Math.max(0, audio.currentTime * 1000));
    audio.onloadedmetadata = () => {
      if (Number.isFinite(audio.duration) && audio.duration > 0) setKnownDurationMs(audio.duration * 1000);
    };
    audio.onerror = () => {
      setPlaying(false);
      setLoadState("error");
    };
    audioRef.current = audio;
    if (source.revoke) ownedUrlRef.current = source.url;
    setLoadState("ready");
    return audio;
  }, [loadSource]);

  const toggle = async () => {
    if (loadState === "loading") return;
    try {
      const audio = await prepareAudio();
      if (audio.paused) await audio.play();
      else audio.pause();
    } catch (error) {
      if (error instanceof Error && error.message === "player_unmounted") return;
      setLoadState("error");
    }
  };

  const totalMs = Math.max(knownDurationMs, 1);
  const progress = Math.min(100, (currentMs / totalMs) * 100);
  const progressStyle = { "--voice-progress": `${progress}%` } as CSSProperties;

  return (
    <div className={`voice-player voice-player-${loadState}`}>
      <button type="button" className="voice-play-button" onClick={() => void toggle()} aria-label={playing ? "Пауза" : label}>
        {loadState === "loading" ? <span className="spinner" /> : <Icon name={playing ? "pause" : "play"} />}
      </button>
      <div className="voice-track">
        <span className="voice-wave" aria-hidden="true">
          {[7, 13, 9, 18, 12, 20, 14, 8, 16, 11, 19, 9, 14, 7].map((height, index) => (
            <i key={index} style={{ height }} />
          ))}
        </span>
        <input
          type="range"
          min={0}
          max={totalMs}
          step={100}
          value={Math.min(currentMs, totalMs)}
          disabled={!audioRef.current}
          onChange={(event) => {
            const audio = audioRef.current;
            if (!audio) return;
            audio.currentTime = Number(event.target.value) / 1000;
            setCurrentMs(Number(event.target.value));
          }}
          aria-label="Позиция голосового сообщения"
          style={progressStyle}
        />
      </div>
      <span className="voice-duration">
        {loadState === "error" ? "Ошибка" : formatDuration(playing ? currentMs : knownDurationMs)}
      </span>
    </div>
  );
}

export function VoiceMessagePlayer({
  gameId,
  messageId,
  durationMs,
}: {
  gameId: string;
  messageId: number;
  durationMs: number;
}) {
  const loadSource = useCallback(async (): Promise<LoadedVoiceSource> => {
    const token = getGameToken(gameId);
    if (!token) throw new Error("Токен игрока не найден.");
    const blob = await getVoiceMessageAudio(gameId, messageId, token);
    return { url: URL.createObjectURL(blob), revoke: true };
  }, [gameId, messageId]);

  return <VoicePlayer durationMs={durationMs} loadSource={loadSource} label="Проиграть голосовое сообщение" />;
}

export function VoiceRecorder({
  gameId,
  disabled,
  onActiveChange,
  onError,
}: {
  gameId: string;
  disabled: boolean;
  onActiveChange: (active: boolean) => void;
  onError: (message: string | null) => void;
}) {
  const [status, setStatus] = useState<RecorderStatus>("idle");
  const [elapsedMs, setElapsedMs] = useState(0);
  const [draft, setDraft] = useState<VoiceDraft | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef(0);
  const finishModeRef = useRef<"preview" | "discard">("preview");
  const requestCancelledRef = useRef(false);
  const draftUrlRef = useRef<string | null>(null);
  const mountedRef = useRef(true);

  const stopTracks = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  const clearDraft = useCallback(() => {
    if (draftUrlRef.current) URL.revokeObjectURL(draftUrlRef.current);
    draftUrlRef.current = null;
    setDraft(null);
  }, []);

  const stopRecording = useCallback((mode: "preview" | "discard") => {
    finishModeRef.current = mode;
    const recorder = recorderRef.current;
    if (recorder?.state === "recording" || recorder?.state === "paused") recorder.stop();
    else {
      stopTracks();
      if (mode === "discard") {
        setElapsedMs(0);
        setStatus("idle");
      }
    }
  }, [stopTracks]);

  useEffect(() => {
    onActiveChange(status !== "idle");
  }, [onActiveChange, status]);

  useEffect(() => {
    if (status !== "recording") return;
    const updateElapsed = () => {
      const next = Math.min(MAX_VOICE_MESSAGE_DURATION_MS, Date.now() - startedAtRef.current);
      setElapsedMs(next);
      if (next >= MAX_VOICE_MESSAGE_DURATION_MS) stopRecording("preview");
    };
    updateElapsed();
    const timer = window.setInterval(updateElapsed, 200);
    return () => window.clearInterval(timer);
  }, [status, stopRecording]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      requestCancelledRef.current = true;
      finishModeRef.current = "discard";
      const recorder = recorderRef.current;
      if (recorder && recorder.state !== "inactive") recorder.stop();
      stopTracks();
      if (draftUrlRef.current) URL.revokeObjectURL(draftUrlRef.current);
      onActiveChange(false);
    };
  }, [onActiveChange, stopTracks]);

  const startRecording = async () => {
    onError(null);
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      onError("Этот браузер не поддерживает запись голосовых сообщений.");
      return;
    }
    const mimeType = preferredMimeType();
    if (!mimeType) {
      onError("Этот браузер не поддерживает подходящий формат голосовых сообщений.");
      return;
    }

    requestCancelledRef.current = false;
    setStatus("requesting");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      if (requestCancelledRef.current || !mountedRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      streamRef.current = stream;
      let recorder: MediaRecorder;
      try {
        recorder = new MediaRecorder(stream, { mimeType, audioBitsPerSecond: 48_000 });
      } catch {
        recorder = new MediaRecorder(stream, { mimeType });
      }
      recorderRef.current = recorder;
      chunksRef.current = [];
      finishModeRef.current = "preview";
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };
      recorder.onerror = () => {
        finishModeRef.current = "discard";
        onError("Запись прервалась. Попробуйте ещё раз.");
      };
      recorder.onstop = () => {
        const mode = finishModeRef.current;
        const durationMs = Math.min(MAX_VOICE_MESSAGE_DURATION_MS, Date.now() - startedAtRef.current);
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || mimeType });
        chunksRef.current = [];
        recorderRef.current = null;
        stopTracks();
        if (!mountedRef.current) return;
        if (mode === "discard") {
          setElapsedMs(0);
          setStatus("idle");
          return;
        }
        if (durationMs < 250 || blob.size === 0) {
          onError("Запишите хотя бы полсекунды.");
          setElapsedMs(0);
          setStatus("idle");
          return;
        }
        if (blob.size > MAX_VOICE_MESSAGE_BYTES) {
          onError("Запись получилась слишком большой. Попробуйте сказать короче.");
          setElapsedMs(0);
          setStatus("idle");
          return;
        }
        clearDraft();
        const url = URL.createObjectURL(blob);
        draftUrlRef.current = url;
        setDraft({ blob, durationMs, url });
        setElapsedMs(durationMs);
        setStatus("preview");
      };
      startedAtRef.current = Date.now();
      setElapsedMs(0);
      recorder.start(250);
      setStatus("recording");
    } catch (error) {
      stopTracks();
      if (!requestCancelledRef.current && mountedRef.current) {
        onError(microphoneError(error));
        setStatus("idle");
      }
    }
  };

  const cancel = () => {
    onError(null);
    if (status === "requesting") {
      requestCancelledRef.current = true;
      stopTracks();
      setStatus("idle");
      return;
    }
    if (status === "recording") {
      stopRecording("discard");
      return;
    }
    clearDraft();
    setElapsedMs(0);
    setStatus("idle");
  };

  const send = async () => {
    if (!draft) return;
    const token = getGameToken(gameId);
    if (!token) {
      onError("Не удалось подтвердить место игрока. Перезагрузите партию.");
      return;
    }
    onError(null);
    setStatus("uploading");
    try {
      await uploadVoiceMessage(gameId, token, draft.blob, draft.durationMs);
      clearDraft();
      setElapsedMs(0);
      setStatus("idle");
    } catch (error) {
      onError(error instanceof Error ? error.message : "Не удалось отправить голосовое сообщение.");
      setStatus("preview");
    }
  };

  if (status === "idle") {
    return (
      <div className="voice-recorder">
        <button
          type="button"
          className="chat-voice-button"
          onClick={() => void startRecording()}
          disabled={disabled}
          aria-label="Отправить голосовое"
          title="Отправить голосовое"
        >
          <Icon name="mic" />
        </button>
      </div>
    );
  }

  if (status === "requesting") {
    return (
      <div className="voice-recorder voice-recorder-active voice-requesting-state" role="status">
        <span className="voice-requesting"><span className="spinner spinner-dark" /> Открываем микрофон…</span>
        <button type="button" className="voice-cancel-button" onClick={cancel} aria-label="Отменить запись"><Icon name="x" /></button>
      </div>
    );
  }

  if (status === "recording") {
    return (
      <div className="voice-recorder voice-recorder-active voice-recording" role="status" aria-label="Идёт запись голосового сообщения">
        <button type="button" className="voice-cancel-button" onClick={cancel} aria-label="Удалить запись"><Icon name="trash" /></button>
        <span className="voice-recording-status"><i aria-hidden="true" /> Запись <time>{formatDuration(elapsedMs)}</time></span>
        <button type="button" className="voice-finish-button" onClick={() => stopRecording("preview")} aria-label="Завершить запись"><Icon name="stop" /></button>
      </div>
    );
  }

  return (
    <div className="voice-recorder voice-recorder-active voice-preview">
      <button type="button" className="voice-cancel-button" onClick={cancel} disabled={status === "uploading"} aria-label="Удалить запись"><Icon name="trash" /></button>
      {draft && (
        <VoicePlayer
          key={draft.url}
          durationMs={draft.durationMs}
          loadSource={async () => ({ url: draft.url, revoke: false })}
          label="Прослушать запись"
        />
      )}
      <button
        type="button"
        className="voice-send-button"
        onClick={() => void send()}
        disabled={status === "uploading"}
        aria-label="Отправить голосовое"
        title="Отправить голосовое"
      >
        {status === "uploading" ? <span className="spinner spinner-light" /> : <Icon name="send" />}
      </button>
    </div>
  );
}
